import ArrowRightIcon from "assets/icons/arrow-double-right.svg?react";
import HamburgerCloseIcon from "assets/icons/hamburger-close.svg?react";
import HamburgerIcon from "assets/icons/hamburger.svg?react";
import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink, useLocation } from "react-router-dom";
import { toggleSidebar } from "redux/slices/sidebarSlice";
import { RootState } from "redux/store/store";
import useHomepage from "pages/Home/hooks/useHomepage";
import { useQuery } from "@tanstack/react-query";
import { getBreakerListByUserId } from "api/SidebarList/sidebarList.api";
import t from "utils/i18n";
import { BreakerListDataType } from "typings/navbarBreakerList";

interface Props {}

const Breadcrumb: FC<Props> = () => {
  const location = useLocation();
  const isSidebarOpen = useSelector(
    (state: RootState) => state.sidebar.isSidebarOpen,
  );
  const dispatch = useDispatch();
  const { selectedGateway } = useHomepage();

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const pathSegments = location.pathname.split("/").filter(Boolean);

  //  Show only up to "settings" for settings pages
  const settingsIndex = pathSegments.findIndex(
    (segment) => segment.toLowerCase() === "settings",
  );
  const effectiveSegments =
    settingsIndex !== -1
      ? pathSegments.slice(0, settingsIndex + 1)
      : pathSegments;

  const isBreakerRoute = pathSegments[0] === "breakers";
  const gatewayId = isBreakerRoute ? pathSegments[1] : null;
  const slotId = isBreakerRoute ? pathSegments[2] : null;

  const { data: breakerListData } = useQuery({
    queryKey: ["breakerList", { gatewayId }],
    queryFn: getBreakerListByUserId,
    enabled: !!gatewayId,
    staleTime: 0,
    refetchOnMount: true,
  });

  const breakerName =
    breakerListData?.find(
      (b: BreakerListDataType) =>
        String(b.id) === slotId && b.gatewayId === gatewayId,
    )?.title || (location.state as any)?.breakerName;

  const getSegmentType = (index: number) => ({
    isBreakersOnly: isBreakerRoute && pathSegments.length === 1,
    isBreakersSegment: isBreakerRoute && index === 0,
    isGatewayIdSegment: isBreakerRoute && index === 1,
    isSlotIdSegment: isBreakerRoute && index === 2,
  });

  const getDisplayText = (segment: string, index: number, segmentType: any) => {
    if (segmentType.isBreakersOnly && index === 0) {
      return t("breadcrumb.gateways");
    }

    if (segmentType.isGatewayIdSegment) {
      return selectedGateway?.label ? String(selectedGateway.label) : null;
    }

    if (segmentType.isSlotIdSegment) {
      return breakerName ? String(breakerName) : null;
    }

    return decodeURIComponent(segment.replace(/-/g, " "));
  };

  const shouldSkipSegment = (segmentType: any) => {
    return !segmentType.isBreakersOnly && segmentType.isBreakersSegment;
  };

  const isUserProvidedName = (segmentType: any) => {
    return segmentType.isGatewayIdSegment || segmentType.isSlotIdSegment;
  };

  const getNavLinkClassName =
    (segmentType: any) =>
    ({ isActive }: { isActive: boolean }) =>
      `${isActive ? "text-interactive-blue" : ""} ${isUserProvidedName(segmentType) ? "" : "capitalize"} py-2`;

  let currentLink = "";
  const crumbs = effectiveSegments
    .map((segment, index) => {
      const segmentType = getSegmentType(index);

      if (shouldSkipSegment(segmentType)) return null;

      const displayText = getDisplayText(segment, index, segmentType);
      if (displayText === null) return null;

      const isLast = index === effectiveSegments.length - 1;

      if (segmentType.isGatewayIdSegment) {
        currentLink = "/home";
      } else {
        currentLink += `/${segment}`;
      }

      return (
        <div
          className="z-50 flex items-center justify-center"
          key={`${segment}-${index}`}
        >
          <ArrowRightIcon className="p-2" name="arrow-right-icon" />
          {isLast ? (
            <span
              className={`${isUserProvidedName(segmentType) ? "" : "capitalize"} py-2 text-[#9999A9] cursor-default`}
            >
              {displayText}
            </span>
          ) : (
            <NavLink
              to={segmentType.isGatewayIdSegment ? "/" : currentLink}
              className={getNavLinkClassName(segmentType)}
            >
              {displayText}
            </NavLink>
          )}
        </div>
      );
    })
    .filter(Boolean);

  return (
    <div className="bg-white flex min-h-[48px] w-screen z-30 fixed top-11 items-center justify-start border-b border-light-gray">
      {/* Sidebar toggle */}
      <span
        onClick={handleToggleSidebar}
        className="flex items-center justify-center cursor-pointer select-none nav-button"
      >
        {isSidebarOpen ? <HamburgerCloseIcon /> : <HamburgerIcon />}
      </span>

      {/* Breadcrumb */}
      <div className="flex items-center justify-between w-full px-2">
        <div className="flex items-center pl-5 text-xs text-interactive-blue">
          <Link to="/">{t("breadcrumb.homepage")}</Link>
          {crumbs}
        </div>

        <div className="flex flex-row items-center pr-5 space-x-5">
          <span className="text-lg font-semibold">
            {/* Welcome, {activeMSALAccountDetails?.given_name ?? ''} */}
          </span>
          {/* <Button variant='Secondary' size='sm' onClick={Logout}>Logout</Button> */}
        </div>
      </div>
    </div>
  );
};

export default Breadcrumb;
